import React from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Customized,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

import {yearScopeEmissions} from '../../../../services/modules/DASHBOARD/yearOnYearEmissions';

import {benchmarkDataIds} from './constants/commonConstants';
import {useOverviewInsight} from './OverviewInsightPanel';
import {
  CategoryAxisTick,
  ChangePill,
  type ChartLayoutProps,
  getChartScales,
  activeCategoryOf,
  HoverHighlight,
  InsightToggleButton,
  SCOPE_SERIES as SERIES,
  SCOPE_SPLIT_FOOTNOTE,
  ScopeLegendItems,
  type ScopeKey,
  ScopeTooltip,
  StickyEmissionsAxis
} from './SustainabilityChartParts';
import SustainabilityInsightPanel from './SustainabilityInsightPanel';

export interface MonthwiseSustainabilityKpiData {
  month: string;
  scope1: number;
  scope2: number;
  scope3: number;
}

interface MonthwiseSustainabilityRow {
  month: string;
  scope1: number | null;
  scope2: number | null;
  scope3: number | null;
}

interface MonthwiseSustainabilityPerformanceProps {
  // Omit to let the card load the company's own month-on-month emissions.
  data?: MonthwiseSustainabilityKpiData[];
  selectedYear?: string;
  // Toggles after emission calculation so this card refetches with the other KPIs.
  refreshKey?: boolean;
}

// The financial year runs April to March, so the axis always starts at April.
const FY_MONTHS = [
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
  'Jan',
  'Feb',
  'Mar'
];

const MONTHS_IN_YEAR = FY_MONTHS.length;

const MIN_VISIBLE_SEGMENT_SHARE = 0.01;

// Widest the hover highlight behind a month's bar may get.
const HIGHLIGHT_MAX_WIDTH = 56;

const TOTAL_MARKER_COLOR = '#024064';

const LABEL_BOX_WIDTH = 100;
const LABEL_BOX_HEIGHT = 64;

// Data is treated as yearly when none of its rows map to a financial-year
// month (e.g. a single row keyed by '2023-24' or carrying annual totals).
const isYearlyData = (source: MonthwiseSustainabilityKpiData[]): boolean =>
  source.length > 0 && !source.some((item) => FY_MONTHS.includes(item.month));

// Yearly totals are spread evenly, so every month shows total / 12.
const spreadYearlyAcrossMonths = (
  source: MonthwiseSustainabilityKpiData[]
): MonthwiseSustainabilityKpiData[] => {
  const totals = source.reduce(
    (acc, item) => ({
      scope1: acc.scope1 + (Number(item.scope1) || 0),
      scope2: acc.scope2 + (Number(item.scope2) || 0),
      scope3: acc.scope3 + (Number(item.scope3) || 0)
    }),
    {scope1: 0, scope2: 0, scope3: 0}
  );

  return FY_MONTHS.map((month) => ({
    month,
    scope1: totals.scope1 / MONTHS_IN_YEAR,
    scope2: totals.scope2 / MONTHS_IN_YEAR,
    scope3: totals.scope3 / MONTHS_IN_YEAR
  }));
};

const getElapsedMonthCount = (selectedYear: string): number => {
  if (!selectedYear) {
    return FY_MONTHS.length;
  }

  const startYear = parseInt(selectedYear.split('-')[0], 10);
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentFullYear = now.getFullYear();
  const currentFyStartYear =
    currentMonth >= 3 ? currentFullYear : currentFullYear - 1;

  if (Number.isNaN(startYear) || startYear !== currentFyStartYear) {
    return FY_MONTHS.length;
  }

  return (currentMonth >= 3 ? currentMonth - 3 : currentMonth + 9) + 1;
};

// "Apr" in FY "2024-2025" -> "Apr 2024"; Jan-Mar fall in the closing year.
const monthWithYear =
  (selectedYear: string) =>
  (month: string): string => {
    const [startYear, endYear] = selectedYear
      .split('-')
      .map((part) => parseInt(part, 10));
    const index = FY_MONTHS.indexOf(month);

    if (Number.isNaN(startYear) || index === -1) {
      return month;
    }

    const year = index >= FY_MONTHS.indexOf('Jan') ? endYear : startYear;

    return Number.isNaN(year) ? month : `${month} ${year}`;
  };

// Emission and percentage figures stay within two decimal places.
const emissionFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const formatEmissionValue = (value: number): string =>
  emissionFormatter.format(value);

const toPlatformCopy = (value: string): string =>
  value
    .replace(/tCO₂e/g, 'tCO2e')
    .replace(/(-?\d+\.\d{3,})/g, (match) => Number(match).toFixed(2));

// Bar values follow the axis' thousands scale: 1,141.5 -> "1.14k".
const formatBarValue = (value: number): string =>
  Math.abs(value) >= 1000
    ? `${emissionFormatter.format(value / 1000)}k`
    : emissionFormatter.format(value);

interface PlotRow extends MonthwiseSustainabilityRow {
  total: number | null;
  drawnTotal: number | null;
  drawnScope1?: number | null;
  drawnScope2?: number | null;
  drawnScope3?: number | null;
  mom: number | null;
  isBaseline: boolean;
}

interface OverlayProps extends ChartLayoutProps {
  rows: PlotRow[];
}

// Change / value pills and the total marker above each bar, positioned from
// the chart's own scales.
const ChartOverlay: React.FC<OverlayProps> = ({rows, ...layout}) => {
  const {xScale, yScale, bandwidth} = getChartScales(layout);

  if (!xScale || !yScale) {
    return null;
  }

  return (
    <g className="yoyPerformanceOverlay" pointerEvents="none">
      {rows.map((row) => {
        if (row.drawnTotal === null || row.total === null) {
          return null;
        }

        const centerX = xScale(row.month) + bandwidth / 2;
        const topY = yScale(row.drawnTotal);

        return (
          <g key={row.month}>
            <foreignObject
              x={centerX - LABEL_BOX_WIDTH / 2}
              y={topY - LABEL_BOX_HEIGHT - 8}
              width={LABEL_BOX_WIDTH}
              height={LABEL_BOX_HEIGHT}
            >
              <div className="yoyPerformanceLabelStack">
                {row.mom !== null ? (
                  <ChangePill change={row.mom} fractionDigits={2} />
                ) : null}
                <span className="yoyPerformanceValue">
                  {formatBarValue(row.total)}
                </span>
              </div>
            </foreignObject>
            <circle
              cx={centerX}
              cy={topY}
              r={4}
              fill="#FFFFFF"
              stroke={TOTAL_MARKER_COLOR}
              strokeWidth={2}
            />
          </g>
        );
      })}
    </g>
  );
};

const MonthwiseSustainabilityPerformance: React.FC<
  MonthwiseSustainabilityPerformanceProps
> = ({data, selectedYear = '', refreshKey = false}) => {
  const insight = useOverviewInsight('trend', selectedYear, refreshKey);
  const [isInsightOpen, setIsInsightOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const [loadedData, setLoadedData] = React.useState<
    MonthwiseSustainabilityKpiData[]
  >([]);
  const [isLoading, setIsLoading] = React.useState(!data);

  React.useEffect(() => {
    if (data) {
      return;
    }

    if (!selectedYear) {
      setLoadedData([]);
      setIsLoading(false);

      return;
    }

    let isActive = true;

    setIsLoading(true);

    yearScopeEmissions(benchmarkDataIds.emissionsScopeWise, selectedYear)
      .then((row) => {
        if (isActive) {
          setLoadedData(
            row
              ? [
                  {
                    month: row.year,
                    scope1: row.scope1,
                    scope2: row.scope2,
                    scope3: row.scope3
                  }
                ]
              : []
          );
        }
      })
      .catch(() => {
        if (isActive) {
          setLoadedData([]);
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    // eslint-disable-next-line consistent-return
    return () => {
      isActive = false;
    };
  }, [data, selectedYear, refreshKey]);

  const source = data ?? loadedData;

  const displayData = React.useMemo<MonthwiseSustainabilityRow[]>(() => {
    if (!source.length) {
      return [];
    }

    // Yearly figures fill all twelve months, so the elapsed-month mask is
    // skipped - every month carries its 1/12 share.
    if (isYearlyData(source)) {
      return spreadYearlyAcrossMonths(source);
    }

    const byMonth = new Map(source.map((item) => [item.month, item]));
    const elapsedMonthCount = getElapsedMonthCount(selectedYear);

    return FY_MONTHS.map((month, index) => {
      if (index >= elapsedMonthCount) {
        return {month, scope1: null, scope2: null, scope3: null};
      }

      return (
        byMonth.get(month) ?? {
          month,
          scope1: null,
          scope2: null,
          scope3: null
        }
      );
    });
  }, [source, selectedYear]);

  // The selected year's reported tCO2e per scope, for the AI insight panel.
  const selectedYearScopes = React.useMemo<
    Record<ScopeKey, number> | undefined
  >(() => {
    if (!source.length) {
      return undefined;
    }

    return source.reduce(
      (acc, item) => ({
        scope1: acc.scope1 + (Number(item.scope1) || 0),
        scope2: acc.scope2 + (Number(item.scope2) || 0),
        scope3: acc.scope3 + (Number(item.scope3) || 0)
      }),
      {scope1: 0, scope2: 0, scope3: 0}
    );
  }, [source]);

  const plotRows = React.useMemo<PlotRow[]>(() => {
    const rowTotal = (row?: MonthwiseSustainabilityRow): number | null => {
      if (row?.scope1 == null || row?.scope2 == null || row?.scope3 == null) {
        return null;
      }

      return row.scope1 + row.scope2 + row.scope3;
    };

    const totals = displayData.map(rowTotal);
    const minVisibleSegment =
      Math.max(0, ...totals.map((total) => total ?? 0)) *
      MIN_VISIBLE_SEGMENT_SHARE;

    // Drawn height of a segment: a non-zero value below the minimum is raised
    // to it so the scope stays visible; the tooltip still shows the real value.
    const visible = (value: number | null): number | null =>
      value !== null && value > 0 && value < minVisibleSegment
        ? minVisibleSegment
        : value;

    return displayData.map((item, index) => {
      const total = totals[index];
      const previousTotal = index > 0 ? totals[index - 1] : null;

      if (total === null) {
        return {
          month: item.month,
          scope1: null,
          scope2: null,
          scope3: null,
          total: null,
          drawnTotal: null,
          mom: null,
          isBaseline: false
        };
      }

      return {
        ...item,
        total,
        drawnTotal:
          (visible(item.scope1) ?? 0) +
          (visible(item.scope2) ?? 0) +
          (visible(item.scope3) ?? 0),
        mom:
          previousTotal === null || previousTotal === 0
            ? null
            : ((total - previousTotal) / previousTotal) * 100,
        isBaseline: false,
        drawnScope1: visible(item.scope1),
        drawnScope2: visible(item.scope2),
        drawnScope3: visible(item.scope3)
      };
    });
  }, [displayData]);

  const activeMonth = activeCategoryOf(
    plotRows,
    activeIndex,
    (row) => row.month
  );

  const formatTooltipLabel = React.useMemo(
    () => monthWithYear(selectedYear),
    [selectedYear]
  );

  const insightResult = React.useMemo(() => {
    const result = insight.result;

    if (result?.state !== 'ready') {
      return result;
    }

    return {
      ...result,
      summary: toPlatformCopy(result.summary),
      suggestion: result.suggestion ? toPlatformCopy(result.suggestion) : null,
      factors: result.factors.map((factor) => ({
        ...factor,
        name: toPlatformCopy(factor.name),
        detail: toPlatformCopy(factor.detail),
        value: toPlatformCopy(factor.value)
      }))
    };
  }, [insight.result]);

  const chartScrollRef = React.useRef<HTMLDivElement>(null);
  const [chartScrollable, setChartScrollable] = React.useState(false);

  React.useEffect(() => {
    const element = chartScrollRef.current;

    if (!element) {
      return undefined;
    }

    const update = () => {
      setChartScrollable(element.scrollWidth > element.clientWidth + 1);
    };

    update();

    const observer = new ResizeObserver(update);
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [isLoading, isInsightOpen, plotRows.length]);

  return (
    <section
      className={`yoyPerformanceCard emissionsTrendCard${
        isInsightOpen ? ' yoyPerformanceCardOpen' : ''
      }`}
    >
      <div className="yoyPerformanceHeader">
        <div className="yoyPerformanceTitle">
          <h2>Emissions Trend</h2>
          <p>
            Total GHG emissions by scope, tCO2e · {selectedYear || 'current fy'}
          </p>
        </div>
        <InsightToggleButton
          open={isInsightOpen}
          onToggle={() => {
            setIsInsightOpen((open) => !open);
          }}
        />
      </div>

      <div className="yoyPerformanceBody">
        <div className="yoyPerformanceChartPanel">
          {isLoading || !plotRows.length ? (
            <div className="yoyPerformanceChartEmpty">
              {isLoading
                ? 'Loading emissions data…'
                : 'No month-wise emissions reported yet.'}
            </div>
          ) : (
            <div className="yoyPerformanceChartFrame">
              <StickyEmissionsAxis
                rows={plotRows}
                xKey="month"
                baseline={null}
              />
              <div
                ref={chartScrollRef}
                className={`yoyPerformanceChartScroll light-scroll${
                  chartScrollable ? ' isScrollable' : ''
                }`}
              >
                <div className="yoyPerformanceChart">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={plotRows}
                      onMouseMove={(state: {activeTooltipIndex?: number}) => {
                        setActiveIndex(state.activeTooltipIndex ?? null);
                      }}
                      onMouseLeave={() => {
                        setActiveIndex(null);
                      }}
                      margin={{top: 72, right: 8, bottom: 0, left: 0}}
                      barSize={42}
                    >
                      <CartesianGrid
                        vertical={false}
                        stroke="rgba(2, 64, 100, 0.1)"
                        strokeDasharray="4 4"
                      />
                      <XAxis
                        dataKey="month"
                        axisLine={{stroke: TOTAL_MARKER_COLOR}}
                        tickLine={false}
                        height={48}
                        interval={0}
                        tick={<CategoryAxisTick />}
                      />
                      <YAxis hide width={0} />
                      <Customized
                        component={
                          <HoverHighlight
                            activeCategory={activeMonth}
                            maxWidth={HIGHLIGHT_MAX_WIDTH}
                          />
                        }
                      />
                      <Tooltip
                        cursor={false}
                        content={
                          <ScopeTooltip
                            portal
                            formatLabel={formatTooltipLabel}
                            formatValue={formatEmissionValue}
                          />
                        }
                      />
                      <Bar
                        dataKey="drawnScope1"
                        stackId="emissions"
                        fill={SERIES[0].color}
                        stroke="#FFFFFF"
                        strokeWidth={1}
                        radius={[2, 2, 0, 0]}
                      />
                      <Bar
                        dataKey="drawnScope2"
                        stackId="emissions"
                        fill={SERIES[1].color}
                        stroke="#FFFFFF"
                        strokeWidth={1}
                        radius={[2, 2, 2, 2]}
                      />
                      <Bar
                        dataKey="drawnScope3"
                        stackId="emissions"
                        fill={SERIES[2].color}
                        stroke="#FFFFFF"
                        strokeWidth={1}
                        radius={[6, 6, 2, 2]}
                      />
                      <Customized
                        component={<ChartOverlay rows={plotRows} />}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          <div className="yoyPerformanceLegend">
            <ScopeLegendItems />
          </div>
          <p className="yoyPerformanceFootnote">{SCOPE_SPLIT_FOOTNOTE}</p>
        </div>

        {isInsightOpen ? (
          <SustainabilityInsightPanel
            status={insight.status}
            result={insightResult}
            scopeValues={selectedYearScopes}
            showMetric
            showShareBar
            formatValue={formatEmissionValue}
          />
        ) : null}
      </div>
    </section>
  );
};

export default MonthwiseSustainabilityPerformance;
